// src/components/layouts/AuthorSidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  FileText, 
  Upload, 
  LogOut,
  Users,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  BookOpen,
  Globe,
  Menu,
  Home,
  Mail,
  User,
  Award,
  Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';

const AuthorSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isActive, setIsActive] = useState(location.pathname);
  
  // State for sidebar collapsed status
  const [isCollapsed, setIsCollapsed] = useState(false);
  // State for mobile menu
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  // State for stats
  const [stats, setStats] = useState({
    submitted: 0,
    inReview: 0,
    coAuthored: 0,
    withDecisions: 0,
    inbox: 0,
    returned: 0,
    draft: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    setStatsError(null);
    
    try {
      const response = await axios.get('/api/authors/dashboard/stats');
      
      if (response.data.status === 'success') {
        setStats(response.data.stats);
      } else {
        setStatsError('Failed to load stats');
        console.error('Error fetching stats:', response.data.message);
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setStatsError(err.response?.data?.message || 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch stats on component mount
  useEffect(() => {
    fetchDashboardStats();
    
    // Optional: Refresh stats every 5 minutes
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Auto-collapse on /submit route
  useEffect(() => {
    if (location.pathname === '/submit') {
      setIsCollapsed(true);
    }
  }, [location.pathname]);

  const navItems = [
    { 
      path: '/dashboard', 
      icon: Home, 
      label: 'Dashboard',
      badge: null,
    },
    { 
      path: '/manuscripts', 
      icon: FileText, 
      label: 'Submitted Manuscripts',
      badge: stats.submitted,
    },
    { 
      path: '/coauthored', 
      icon: Users, 
      label: 'Co-Authored Manuscripts',
      badge: stats.coAuthored,
    },
    { 
      path: '/submit', 
      icon: Upload, 
      label: 'Submit New Manuscript',
      badge: null,
    },
    { 
      path: '/inreview', 
      icon: ClipboardCheck, 
      label: 'Submit Returning Manuscript',
      badge: stats.withDecisions || 0,
    },
    { 
      path: '/messages/inbox', 
      icon: Mail, 
      label: 'Inbox',
      badge: stats.inbox,
    },
    { 
      path: '/settings', 
      icon: User, 
      label: 'Settings',
      badge: null,
    },
  ];

  const handleLogout = async () => {  
    await logout();
    navigate('/portal/login');
  };

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Close sidebar on mobile
  const closeSidebar = () => {
    setIsMobileOpen(false);
  };

  // Get user initials
  const getUserInitials = () => {
    if (!user) return 'DT';
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    if (user?.fullname) {
      const names = user.fullname.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`;
      }
      return names[0][0];
    }
    return 'DT';
  };

  // Get user full name
  const getUserFullName = () => {
    if (!user) return 'Author User';
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.fullname) {
      return user.fullname;
    }
    return 'Author User';
  };

  // Get user ORCID
  const getUserOrcid = () => {
    if (!user) return '0000-0002-3458-8696';
    return user?.orcidId || user?.orcid || '0000-0002-3458-8696';
  };

  // Get user affiliation
  const getUserAffiliation = () => {
    if (!user) return '';
    return user?.affiliations || user?.affiliation || '';
  };

  return (
    <>
      {/* Mobile Menu Button (visible on small screens) */}
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-20 left-4 z-50 bg-purple-600 text-white p-2 rounded-lg shadow-lg"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}

      <aside 
        className={`
          ${isCollapsed ? 'w-20' : 'w-72'} 
          bg-white shadow-xl
          fixed lg:relative
          ${isMobileOpen ? 'left-0' : '-left-72 lg:left-0'}
          top-0 h-screen z-50
          flex flex-col
          transition-all duration-300 ease-in-out
        `}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              {isCollapsed ? (
                <div className="w-full flex justify-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center">
                    <img src="/assets/images/favicon.png" alt="ASFI" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center flex-shrink-0">
                    <img src="/assets/images/favicon.png" alt="ASFI" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-800 truncate">Author Dashboard</h2>
                    <p className="text-xs text-gray-500 truncate">Manage submissions</p>
                  </div>
                </div>
              )}
              
              {/* Collapse toggle button - visible on lg screens */}
              <button 
                onClick={toggleSidebar}
                className="hidden lg:flex text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
              >
                {isCollapsed ? 
                  <ChevronRight className="w-5 h-5" /> : 
                  <ChevronLeft className="w-5 h-5" />
                }
              </button>
              
              {/* Close button for mobile */}
              <button 
                onClick={closeSidebar}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* User Profile Summary - Fixed section with original styling */}
          <div className="px-4 py-4 border-b border-gray-200 flex-shrink-0">
            <div className={`flex ${isCollapsed ? 'flex-col items-center' : 'items-center'} space-y-${isCollapsed ? '2' : '0'} p-3 bg-gray-50 rounded-lg`}>
              <div className="w-12 h-12 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-white font-semibold text-lg">
                  {getUserInitials()}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0 ml-3">
                  <h3 className="font-semibold text-gray-800 truncate">
                    {getUserFullName()}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 truncate flex items-center">
                    {user?.orcidId && <Award className="w-3 h-3 mr-1 text-[--asfi-blue]" />}
                    ORCID: {getUserOrcid()}
                  </p>
                  {/* FIXED: Only render if affiliation exists and is a string */}
                  {getUserAffiliation() && (
                    <p className="text-xs text-gray-400 mt-1 truncate" title={getUserAffiliation()}>
                      {getUserAffiliation()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation - Scrollable area */}
          <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <ul className="space-y-1 px-3">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-3 rounded-lg transition-all group relative ${
                        isActive 
                          ? 'bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white shadow-sm' 
                          : 'text-gray-700 hover:bg-purple-50'
                      }`
                    }
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center">
                      <item.icon className={`w-5 h-5 flex-shrink-0 ${
                        isActive ? 'text-purple' : 'text-purple-500 group-hover:text-[--asfi-blue]'
                      }`} />
                      {!isCollapsed && (
                        <span className="ml-3 text-sm font-medium truncate">{item.label}</span>
                      )}
                    </div>
                    {!isCollapsed && item.badge !== null && item.badge > 0 && (
                      <span className={`ml-2 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold ${
                        isActive
                          ? 'bg-white text-purple-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                    {isCollapsed && item.badge !== null && item.badge > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Help Box - Inside scrollable area with original styling */}
            {!isCollapsed && (
              <div className="mt-6 px-3">
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-800 text-lg mb-4 flex items-center">
                    <HelpCircle className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                    Help
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <a 
                        href="https://asfirj.org/authors.html" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-[--asfi-blue] hover:text-[--asfi-purple] transition-colors"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Author Instructions
                        <span className="ml-auto text-xs opacity-60">↗</span>
                      </a>
                    </li>
                    <li>
                      <a 
                        href="https://asfirj.org" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-[--asfi-blue] hover:text-[--asfi-purple] transition-colors"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Journal Website
                        <span className="ml-auto text-xs opacity-60">↗</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Quick Stats - Inside scrollable area */}
            {!isCollapsed && (
              <div className="mt-4 px-3">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <i className="bi bi-graph-up mr-2 text-[--asfi-blue]"></i> Quick Stats
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Submitted</span>
                      <span className="font-medium text-purple-700">{stats.submitted}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">In Review</span>
                      <span className="font-medium text-purple-700">{stats.inReview}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Co-Authored</span>
                      <span className="font-medium text-purple-700">{stats.coAuthored}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Inbox</span>
                      <span className="font-medium text-purple-700">{stats.inbox}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </nav>

          {/* Logout Button - Fixed at bottom with original styling */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleLogout}
              className={`flex items-center ${isCollapsed ? 'justify-center' : ''} w-full px-4 py-3 rounded-lg hover:bg-red-50 transition-colors group`}
              title={isCollapsed ? 'Log Out' : ''}
            >
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${
                isCollapsed ? '' : 'mr-3'
              }`}>
                <i className="bi bi-box-arrow-right text-red-500 text-lg"></i>
              </span>
              {!isCollapsed && (
                <span className="text-sm font-medium text-gray-700 group-hover:text-red-600">Log Out</span>
              )}
            </button>

            {/* Footer text - Only when expanded */}
            {!isCollapsed && (
              <div className="text-center mt-4">
                <p className="text-xs text-gray-500">
                  Managed by Weperch Technologies
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  &copy; {new Date().getFullYear()} ASFIRJ
                </p>
                {user?.asfiMembershipId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Member ID: {user.asfiMembershipId}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default AuthorSidebar;